// components/portal/SourceModeChip.tsx
//
// The Source composer's mode picker: a coloured chip on the composer row, and a small popover
// headed "Mode" listing the three positions with the active one checked.
//
// It self-gates on the session rather than taking a prop, the way the Studio models control next to
// it does. The composer is shared by Studio, Analyst, Slides, Master and Source, and a prop would
// have meant every one of those call sites learning about a mode only one of them has. Ask the
// session what it is instead: `resolveSourceAgentMode` returns null for everything that is not
// Source, and the chip renders nothing.
//
// What each position actually does lives in lib/leasing/sourceAgentMode.ts, next to the allowlist
// and the denylist. Nothing here decides anything; it only writes the session's collaboration mode,
// which is the field the whole app already reads.
import React, { useCallback, useEffect, useRef } from 'react';
import { ModeChip } from '@/components/ui/ModeChip';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import {
  COLLABORATION_MODE_BY_SOURCE_AGENT_MODE,
  normalizeSourceAgentMode,
  resolveSourceAgentMode,
  SOURCE_AGENT_MODES,
  type SourceAgentMode,
} from '#lib/leasing/sourceAgentMode';

type ModeCopy = { label: string };

const COPY: Record<'en' | 'fr', { header: string; modes: Record<SourceAgentMode, ModeCopy> }> = {
  en: {
    header: 'Mode',
    modes: {
      MANUAL: { label: 'Manual' },
      ACCEPT_EDITS: { label: 'Accept edits' },
      AUTO: { label: 'Auto' },
    },
  },
  fr: {
    header: 'Mode',
    modes: {
      MANUAL: { label: 'Manuel' },
      ACCEPT_EDITS: { label: 'Accepter les modifications' },
      AUTO: { label: 'Auto' },
    },
  },
};

export const SourceModeChip: React.FC<{ disabled?: boolean }> = ({ disabled = false }) => {
  const { kernel } = useKernel();
  const lang: 'en' | 'fr' = useKernelStore((state) => (state.language === 'fr' ? 'fr' : 'en'));
  const sessionId = useKernelStore((state) => state.activeChatSessionId);
  // Null for every session the picker does not govern, which is what hides the chip everywhere
  // except Source.
  const mode = useKernelStore((state) => {
    const id = state.activeChatSessionId;
    return resolveSourceAgentMode(id ? state.inventory.ChatSession.registry[id] : null);
  });
  const rememberedMode = useKernelStore((state) =>
    normalizeSourceAgentMode(state.userPreferences.sourceAgentMode)
  );

  const writeSessionMode = useCallback(
    (next: SourceAgentMode) => {
      // The same write the Studio composer performs, through the same Kernel entrypoint: an
      // optimistic merge into kernel state, then the session's own debounced autosave carries it to
      // the ChatSession row. No new persistence path, and no new field.
      kernel.current?.setSessionCollaborationMode(
        typeof sessionId === 'string' ? sessionId : '',
        COLLABORATION_MODE_BY_SOURCE_AGENT_MODE[next]
      );
    },
    [kernel, sessionId]
  );

  // The choice follows the user, not the shortlist: a leasing manager who switched to Accept edits
  // this morning should not be back in Manual because they opened another asset. The session still
  // owns the decision (that is what the agent reads); the preference only says which mode a Source
  // session opens on. Once per session, and a no-op when they already agree, which they do as soon
  // as the user has touched the chip once.
  const syncedSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId || !mode || !rememberedMode) return;
    if (syncedSessionRef.current === sessionId) return;
    syncedSessionRef.current = sessionId;
    if (rememberedMode === mode) return;
    writeSessionMode(rememberedMode);
  }, [sessionId, mode, rememberedMode, writeSessionMode]);

  const selectMode = useCallback(
    (next: SourceAgentMode) => {
      writeSessionMode(next);
      void kernel.current?.updateAndSaveUserPreference('sourceAgentMode', next);
      kernel.current?.logUserAction?.('Source Agent Mode Changed', { sessionId, mode: next });
    },
    [kernel, sessionId, writeSessionMode]
  );

  const copy = COPY[lang];

  if (!mode) return null;

  return (
    <ModeChip
      modes={SOURCE_AGENT_MODES}
      mode={mode}
      labels={{ MANUAL: copy.modes.MANUAL.label, ACCEPT_EDITS: copy.modes.ACCEPT_EDITS.label, AUTO: copy.modes.AUTO.label }}
      header={copy.header}
      onSelect={selectMode}
      disabled={disabled}
      pill
    />
  );
};

SourceModeChip.displayName = 'SourceModeChip';
